// Shared column-layout (resize + reorder) for the multi-tabular entry grids
// — both the main grid (EmbedCaptureTabularPreview) and the inline child grid
// (ChildTabularGrid). It mirrors the transaction designer's GridPreview
// (transaction/PreviewPanel.tsx): native-DnD header reorder with a left/right
// drop indicator + pointer-drag width resize.
//
// Unlike the designer (which writes straight to the model), the preview/run
// grids keep the change as a LOCAL override per form_no — so reordering and
// resizing work for *every* viewer (preview AND the deployed run) without
// touching the design. An author additionally gets an "Apply to design"
// action (host-owned) that bakes the override's width + x back into the
// transaction model, persisting it via saveTransactionObject. `x` is purely a
// grid sort key (the form-input layout orders by `y`), so reassigning it as
// (visualIndex + 1) * 10 — exactly what the designer's reorder does — never
// disturbs the single-record form layout.

import { useCallback, useRef, useState } from 'react'
import type {
  CSSProperties, DragEvent as RDragEvent, HTMLAttributes, MouseEvent as RMouseEvent,
} from 'react'
import type { ColumnDef } from '../types'
import { columnFormKey } from '../utils'

/** Stable per-column key — col_id when present, else the form (db) key.
 *  Matches the React keys both grids already use for <col>/<th>. */
export function colKeyOf(col: ColumnDef): string {
  return col.col_id || columnFormKey(col)
}

/** Designer width (px) with the same floor/default the grids use. */
export function baseColWidth(col: ColumnDef): number {
  const w = Number(col.width)
  if (Number.isFinite(w) && w > 0) return Math.max(40, Math.floor(w))
  return 120
}

export interface GridLayout {
  /** colKey → overridden width (px). */
  widths: Record<string, number>
  /** Explicit display order (colKeys) or null when following the model's x order. */
  order: string[] | null
}

/** API the host (EmbedCaptureTabularPreview) hands to each grid so the grid can
 *  read/mutate its own (per-form_no) layout override and trigger persistence. */
export interface GridLayoutApi {
  layouts: Record<string, GridLayout>
  setWidth: (formNo: string, colKey: string, width: number) => void
  setOrder: (formNo: string, keys: string[]) => void
  reset: (formNo: string) => void
  apply: (formNo: string) => void
  /** Whether this viewer may persist layout changes into the design. */
  canEdit: boolean
  /** form_no currently being persisted (for a spinner / disabled state). */
  applyingFormNo: string | null
  /** Button label + tooltip for "Apply" — varies by persistence target
   *  ("Apply layout" → design JSON vs "Save to customization" → tenant overlay). */
  applyLabel?: string
  applyTitle?: string
}

export function layoutIsDirty(layout: GridLayout | undefined | null): boolean {
  if (!layout) return false
  return layout.order != null || Object.keys(layout.widths).length > 0
}

/** Effective width: override if set, else the designer width. */
export function widthOf(col: ColumnDef, layout?: GridLayout | null): number {
  const o = layout?.widths[colKeyOf(col)]
  if (o != null && Number.isFinite(o) && o > 0) return Math.max(40, Math.floor(o))
  return baseColWidth(col)
}

/** Apply the order override on top of the model-ordered base columns. Unknown
 *  keys are skipped; columns missing from the override are appended in their
 *  base order (so a freshly-added column still shows up). */
export function orderedDisplayCols(
  baseCols: ColumnDef[], layout?: GridLayout | null,
): ColumnDef[] {
  const order = layout?.order
  if (!order || order.length === 0) return baseCols
  const byKey = new Map(baseCols.map((c) => [colKeyOf(c), c]))
  const out: ColumnDef[] = []
  for (const k of order) {
    const c = byKey.get(k)
    if (c) { out.push(c); byKey.delete(k) }
  }
  for (const c of baseCols) if (byKey.has(colKeyOf(c))) out.push(c)
  return out
}

export const RESIZE_HANDLE_STYLE: CSSProperties = {
  position: 'absolute', right: 0, top: 0, bottom: 0, width: 6,
  cursor: 'col-resize', background: 'transparent', userSelect: 'none',
}

/** What a grid needs to render the resize/reorder gestures + optional apply. */
export interface ResolvedLayout {
  layout: GridLayout | undefined
  setWidth: (colKey: string, width: number) => void
  setOrder: (keys: string[]) => void
  reset: () => void
  /** null when this surface can't persist into the design (no host wired, or
   *  the viewer isn't an author) — resize/reorder still work, just no "Apply". */
  apply: (() => void) | null
  applying: boolean
  dirty: boolean
  /** Optional "Apply" button label + tooltip (set by the host's persistence
   *  mode); the grids fall back to the default design wording when unset. */
  applyLabel?: string
  applyTitle?: string
}

/** Resolve a grid's layout source: a host-provided (persistable) override keyed
 *  by `formNo`, OR a self-contained LOCAL override when no host is wired. This
 *  lets column resize/reorder work on EVERY surface (the local fallback), while
 *  "Apply to design" only appears where a host supplied `useGridLayout`. */
export function useResolvedGridLayout(
  external: GridLayoutApi | undefined,
  formNo: string | undefined,
): ResolvedLayout {
  const [local, setLocal] = useState<GridLayout>({ widths: {}, order: null })
  if (external && formNo) {
    const layout = external.layouts[formNo]
    return {
      layout,
      setWidth: (k, w) => external.setWidth(formNo, k, w),
      setOrder: (keys) => external.setOrder(formNo, keys),
      reset: () => external.reset(formNo),
      apply: external.canEdit ? () => external.apply(formNo) : null,
      applying: external.applyingFormNo === formNo,
      dirty: layoutIsDirty(layout),
      applyLabel: external.applyLabel,
      applyTitle: external.applyTitle,
    }
  }
  return {
    layout: local,
    setWidth: (k, w) => setLocal((p) => ({ ...p, widths: { ...p.widths, [k]: w } })),
    setOrder: (keys) => setLocal((p) => ({ ...p, order: keys })),
    reset: () => setLocal({ widths: {}, order: null }),
    apply: null,
    applying: false,
    dirty: layoutIsDirty(local),
  }
}

function cssAttrEscape(v: string): string {
  const c = (globalThis as { CSS?: { escape?: (s: string) => string } }).CSS
  if (c?.escape) return c.escape(v)
  return v.replace(/(["\\])/g, '\\$1')
}

export interface ColumnDnd {
  dropIndicator: { key: string; side: 'left' | 'right' } | null
  getHeaderProps: (colKey: string) => HTMLAttributes<HTMLElement>
  getResizeHandleProps: (colKey: string) => HTMLAttributes<HTMLDivElement>
}

/** Native-DnD reorder + pointer-drag resize for a grid's header row. The caller
 *  owns the order/width state; this hook only emits the gestures and calls
 *  back. Resize updates the live DOM (th/col/table widths) during the drag for
 *  smoothness and commits the final width to state on mouse-up. */
export function useColumnDnd(opts: {
  displayKeys: string[]
  enabled: boolean
  onReorder: (keys: string[]) => void
  onResize: (colKey: string, width: number) => void
}): ColumnDnd {
  const { enabled, onReorder, onResize } = opts
  const draggedKeyRef = useRef<string | null>(null)
  const [dropIndicator, setDropIndicator] =
    useState<{ key: string; side: 'left' | 'right' } | null>(null)
  // Set while a resize drag is active so a 5px move doesn't also fire the th's
  // native dragstart and clobber the resize (mirrors PreviewPanel).
  const resizingFlagRef = useRef(false)
  // Latest order for the drop handler without recreating the callback per row.
  const keysRef = useRef(opts.displayKeys)
  keysRef.current = opts.displayKeys

  const getHeaderProps = useCallback((colKey: string): HTMLAttributes<HTMLElement> => {
    if (!enabled) return {}
    return {
      draggable: true,
      onDragStart: (e: RDragEvent) => {
        if (resizingFlagRef.current) { e.preventDefault(); return }
        draggedKeyRef.current = colKey
        e.dataTransfer.effectAllowed = 'move'
        ;(e.currentTarget as HTMLElement).style.opacity = '0.5'
      },
      onDragEnd: (e: RDragEvent) => {
        ;(e.currentTarget as HTMLElement).style.opacity = '1'
        draggedKeyRef.current = null
        setDropIndicator(null)
      },
      onDragOver: (e: RDragEvent) => {
        if (draggedKeyRef.current == null) return
        e.preventDefault()
        e.dataTransfer.dropEffect = 'move'
        const rect = (e.currentTarget as HTMLElement).getBoundingClientRect()
        const side: 'left' | 'right' =
          (e.clientX - rect.left) < rect.width / 2 ? 'left' : 'right'
        setDropIndicator((prev) =>
          prev && prev.key === colKey && prev.side === side ? prev : { key: colKey, side })
      },
      onDrop: (e: RDragEvent) => {
        e.preventDefault()
        const from = draggedKeyRef.current
        draggedKeyRef.current = null
        setDropIndicator(null)
        if (from == null || from === colKey) return
        const rect = (e.currentTarget as HTMLElement).getBoundingClientRect()
        const insertBefore = (e.clientX - rect.left) < rect.width / 2
        const keys = keysRef.current.filter((k) => k !== from)
        const target = keys.indexOf(colKey)
        if (target < 0) return
        keys.splice(insertBefore ? target : target + 1, 0, from)
        onReorder(keys)
      },
    }
  }, [enabled, onReorder])

  const getResizeHandleProps = useCallback((colKey: string): HTMLAttributes<HTMLDivElement> => {
    if (!enabled) return {}
    return {
      draggable: false,
      onDragStart: (e: RDragEvent) => { e.preventDefault(); e.stopPropagation() },
      onClick: (e: RMouseEvent) => e.stopPropagation(),
      onMouseDown: (e: RMouseEvent) => {
        e.preventDefault(); e.stopPropagation()
        const handle = e.currentTarget as HTMLElement
        const th = handle.closest('th') as HTMLElement | null
        const table = handle.closest('table') as HTMLTableElement | null
        if (!th || !table) return
        const colEl = table.querySelector<HTMLElement>(
          `colgroup col[data-col-key="${cssAttrEscape(colKey)}"]`,
        )
        const startX = e.pageX
        const startWidth = th.offsetWidth
        const startTableWidth = table.offsetWidth
        resizingFlagRef.current = true
        document.body.style.cursor = 'col-resize'
        document.body.style.userSelect = 'none'
        let finalWidth = startWidth
        const move = (ev: MouseEvent) => {
          finalWidth = Math.max(40, startWidth + (ev.pageX - startX))
          th.style.width = `${finalWidth}px`
          if (colEl) colEl.style.width = `${finalWidth}px`
          table.style.width = `${Math.max(40, startTableWidth + (finalWidth - startWidth))}px`
        }
        const up = (ev: MouseEvent) => {
          document.removeEventListener('mousemove', move)
          document.removeEventListener('mouseup', up)
          document.body.style.cursor = ''
          document.body.style.userSelect = ''
          if (Math.abs(ev.pageX - startX) >= 1) onResize(colKey, finalWidth)
          // Release a tick later so an in-flight dragstart stays suppressed.
          setTimeout(() => { resizingFlagRef.current = false }, 0)
        }
        document.addEventListener('mousemove', move)
        document.addEventListener('mouseup', up)
      },
    }
  }, [enabled, onResize])

  return { dropIndicator, getHeaderProps, getResizeHandleProps }
}
